import { useEffect, useRef, useState, type CSSProperties, type FormEvent } from "react";
import { useMatches, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Icon } from "@majourneys/icons";
import { activityKeys } from "../../activities/constants/query-keys";
import { useAiChat, type ChatLang } from "../hooks/use-ai-chat";
import { useChatHistory } from "../hooks/use-chat-history";
import { useThreadAnchor } from "../hooks/use-thread-anchor";
import { AssistantTurn, ChatError, PendingTurn, UserBubble } from "./assistant-turn";
import { AiPillResume, useResumeDismissed } from "./ai-pill-resume";
import { ChatHistoryMenu } from "./chat-history-menu";
import { ChatToolbar } from "./chat-toolbar";
import { ConciergeSignInGate } from "./concierge-sign-in-gate";
import { MatchPreviewProvider } from "./match-preview";
import { SOFT } from "./soft-tokens";
import { conciergeActions, useConciergeStore } from "../state/concierge-store";
import { pewterFrame, pewterInner, solid } from "../../../shared/ui/pewter-edge";

/** Pill in the house pewter band; the send disc wears the arrows' sheen. */
const BAR_FRAME = pewterFrame(999, { gap: 3 });
const BAR_STYLE: CSSProperties = {
  ...BAR_FRAME,
  boxShadow: `${BAR_FRAME.boxShadow}, 0 14px 36px -14px rgba(17,14,57,0.35)`,
};
const BAR_INNER = pewterInner(solid("rgba(255,255,255,1)"), 999, { gap: 3 });
/**
 * Scrolled down, the pill turns to frosted glass inside its pewter band. The
 * glass is always there underneath; only the white fill above it fades, so the
 * band stays put and nothing swaps gradients (which would snap, not fade).
 *
 * The band is the frame masked down to its border. Without the mask the
 * frame's own gradient fills the middle too, and the glass would show pewter
 * rather than the page.
 */
const BAR_GLASS: CSSProperties = {
  background: "rgba(255,255,255,0.5)",
  backdropFilter: "blur(18px) saturate(1.5)",
  WebkitBackdropFilter: "blur(18px) saturate(1.5)",
  boxShadow: BAR_STYLE.boxShadow,
};
const RING_MASK = "linear-gradient(#000 0 0), linear-gradient(#000 0 0)";
const BAR_RING: CSSProperties = {
  ...BAR_FRAME,
  boxShadow: undefined,
  // Prefixed first: Chrome treats -webkit-mask-composite as an alias, so the
  // standard value has to come last to win there.
  WebkitMaskImage: RING_MASK,
  WebkitMaskClip: "padding-box, border-box",
  WebkitMaskComposite: "xor",
  maskImage: RING_MASK,
  maskClip: "padding-box, border-box",
  maskComposite: "exclude",
};
const SEND_FRAME = pewterFrame(999, { gap: 2, style: "sheen" });
const SEND_INNER = pewterInner(solid("rgba(255,255,255,1)"), 999, { gap: 2 });

/**
 * The chat window and the ideas panel: the house 4px band (owner, 2026-10-11:
 * every panel's band is the same width), on the chat's pewter ground.
 */
const WINDOW_RADIUS = 24;
const WINDOW_FRAME = pewterFrame(WINDOW_RADIUS);
const WINDOW_STYLE: CSSProperties = {
  ...WINDOW_FRAME,
  boxShadow: `${WINDOW_FRAME.boxShadow}, 0 24px 60px -24px rgba(17,14,57,0.4)`,
};
const WINDOW_INNER = pewterInner(solid(SOFT.ground), WINDOW_RADIUS);

/**
 * Pages where the pill stands aside: the chat's own pages, where a second
 * composer would compete with the real one, and the booking flow from
 * packages to thank-you, where the guest should be finishing a booking, and
 * the application forms (ambassadors, creators, partners), where the bar
 * covers the form and has nothing to offer someone applying (owner,
 * 2026-10-09).
 */
const HIDDEN_ROUTES = new Set<string>([
  "/$lang/_layout/ambassadors",
  "/$lang/_layout/creators",
  "/$lang/_layout/become-a-partner",
  "/$lang/_layout/ai-chat",
  "/$lang/_layout/ai-travel-planner",
  "/$lang/_layout/concierge",
  "/$lang/_layout/discover",
  /* The rest of the Discover shell: its rail already has MaJourneys AI, and
     the pill sat on the trip's last day (owner, 2026-10-11). */
  "/$lang/_layout/trips",
  "/$lang/_layout/liked",
  "/$lang/_layout/trip-planner",
  "/$lang/_layout/chat/$token",
  "/$lang/_layout/$city/$category/$slug/packages",
  "/$lang/_layout/$slug/packages",
  "/$lang/_layout/$slug/details",
  "/$lang/_layout/$slug/checkout",
  "/$lang/_layout/bucket",
  /* The ride form already offers WhatsApp; nothing floats over it (owner, 2026-10-10). */
  "/$lang/_layout/transport",
  "/$lang/_layout/transport_/$slug",
  "/$lang/_layout/booking/$bookingId/thank-you",
  "/$lang/_layout/stays/checkout",
  "/$lang/_layout/stays/booking/$reference",
  "/$lang/_layout/stays/confirmation/$reference",
  "/$lang/_layout/stays/receipt/$reference",
]);

const HOME_ROUTE = "/$lang/_layout/";
const ACTIVITY_ROUTES = new Set<string>(["/$lang/_layout/$city/$category/$slug/", "/$lang/_layout/$slug/"]);
const CITY_ROUTES = new Set<string>([
  "/$lang/_layout/place/$city",
  "/$lang/_layout/explore/$city/",
  "/$lang/_layout/category/$category/$city",
  "/$lang/_layout/stays/$city/",
]);

type PageContext =
  | { kind: "home" }
  | { kind: "activity"; slug: string }
  | { kind: "city"; city: string }
  | { kind: "other" };

interface Idea {
  label: string;
  query: string;
}

function prettySlug(slug: string): string {
  return slug
    .split("-")
    .map((w) => (w ? w[0]!.toUpperCase() + w.slice(1) : w))
    .join(" ");
}

function ideasFor(ctx: PageContext, t: TFunction, activityTitle: string | null): Idea[] {
  if (ctx.kind === "activity" && activityTitle) {
    const title = activityTitle;
    return (["kids", "wear", "cheaper", "best_time"] as const).map((k) => ({
      label: t(`ai_pill.ideas.activity_${k}_label`),
      query: t(`ai_pill.ideas.activity_${k}_query`, { title }),
    }));
  }
  if (ctx.kind === "city") {
    const city = prettySlug(ctx.city);
    return (["tonight", "two_days", "hidden", "eat"] as const).map((k) => {
      const text = t(`ai_pill.ideas.city_${k}`, { city });
      return { label: text, query: text };
    });
  }
  return (["plan", "desert", "rooftop", "hammam"] as const).map((k) => {
    const text = t(`ai_pill.ideas.general_${k}`);
    return { label: text, query: text };
  });
}

/**
 * MaJourneys AI pill, pinned to the bottom of every public page on desktop
 * (shop.app pattern). Mounted once in the `$lang` layout, so a conversation follows the
 * guest from page to page; the thread itself lives in the shared chat history,
 * so it also survives a reload and opens on the AI Chat page.
 *
 * Unmounts on the chat's own pages and on the booking flow. Unmounting rather
 * than hiding is deliberate: the AI Chat page writes to the same history, and a
 * fresh mount rehydrates from it instead of showing a stale thread.
 */
export function AiPill() {
  const matches = useMatches();
  const ids = matches.map((m) => m.routeId as string);
  if (ids.some((id) => HIDDEN_ROUTES.has(id))) return null;

  const params = (matches[matches.length - 1]?.params ?? {}) as Record<string, string | undefined>;
  let ctx: PageContext = { kind: "other" };
  if (ids.includes(HOME_ROUTE)) ctx = { kind: "home" };
  else if (ids.some((id) => ACTIVITY_ROUTES.has(id)) && params.slug) ctx = { kind: "activity", slug: params.slug };
  else if (ids.some((id) => CITY_ROUTES.has(id)) && params.city) ctx = { kind: "city", city: params.city };

  return <AiPillBar ctx={ctx} />;
}

function AiPillBar({ ctx }: { ctx: PageContext }) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language?.slice(0, 2) ?? "en") as ChatLang;
  const isRtl = lang === "ar";
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { pastHero } = useConciergeStore();
  const [chatOpen, setChatOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [scrolledDown, setScrolledDown] = useState(false);
  const [query, setQuery] = useState("");
  const [lastQuery, setLastQuery] = useState("");
  const [inTrip, setInTrip] = useState<Set<string>>(new Set());
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const windowRef = useRef<HTMLDivElement>(null);

  const history = useChatHistory();
  const { messages, loading, error, send, setMessages, gated, gatedQuery, clearGate } = useAiChat({
    lang,
    limit: 20,
  });

  // Home keeps its own big search up top; the pill waits until it has scrolled
  // away. An open conversation keeps the pill up regardless.
  const visible = ctx.kind !== "home" || pastHero || chatOpen;

  // The "Plan with AI" corner launcher steps aside while the pill is up. The
  // pill is desktop only (owner, 2026-10-05: phones already have the tab bar
  // and the launcher), so on a phone the launcher stays.
  useEffect(() => {
    const desktop = window.matchMedia("(min-width: 1024px)").matches;
    conciergeActions.setFloatingBarVisible(visible && desktop);
  }, [visible]);
  useEffect(() => () => conciergeActions.setFloatingBarVisible(false), []);

  // Hydrate once from saved history, then persist; same guard as the modal.
  const hydratedRef = useRef(false);
  const { persistMessages, activeMessages, loaded } = history;
  useEffect(() => {
    if (hydratedRef.current || !loaded) return;
    if (activeMessages.length > 0) setMessages(activeMessages);
    hydratedRef.current = true;
  }, [loaded, activeMessages, setMessages]);
  useEffect(() => {
    if (!hydratedRef.current || messages.length === 0) return;
    persistMessages(messages);
  }, [messages, persistMessages]);

  useThreadAnchor(scrollerRef, messages, loading);

  // Same rhythm as the phone's tab bar: shrink on the way down, come back on
  // the way up or near the top. Read on the event, not on a frame, for the
  // reason in shared/layout/header.tsx (iOS throttles rAF mid-fling).
  useEffect(() => {
    let lastY = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      const dy = y - lastY;
      if (y < 80) setScrolledDown(false);
      else if (Math.abs(dy) > 8) setScrolledDown(dy > 0);
      else return;
      lastY = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!chatOpen && !focused) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setChatOpen(false);
      inputRef.current?.blur();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [chatOpen, focused]);

  const ask = (text: string) => {
    const q = text.trim();
    if (!q || loading) return;
    setLastQuery(q);
    setQuery("");
    setChatOpen(true);
    void send(q);
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!query.trim()) {
      // Empty send reopens the thread when there is one to show.
      if (messages.length > 0) setChatOpen(true);
      return;
    }
    ask(query);
  };

  const toggleTrip = (id: string) =>
    setInTrip((s) => {
      const next = new Set(s);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const resumeAfterSignIn = () => {
    const pending = gatedQuery;
    clearGate();
    if (pending) setQuery(pending);
    window.setTimeout(() => inputRef.current?.focus(), 0);
  };

  const lastUserText = [...messages].reverse().find((m) => m.kind === "user");
  const resumeText = lastUserText && lastUserText.kind === "user" ? lastUserText.text : null;
  const canResume = !chatOpen && resumeText !== null;
  // "Continue your chat" until the guest hides it; hiding is per question, so
  // the next question they ask brings it back.
  const [resumeDismissed, dismissResume] = useResumeDismissed(lastUserText?.id ?? null);
  const resuming = canResume && !resumeDismissed && !query && !focused;
  // With the composer disabled by the sign-in gate, the send button is the
  // only way back into the thread, so it stays a reopen button.
  const reopenOnly = canResume && !query && gated;
  const reopen = () => {
    setChatOpen(true);
    window.setTimeout(() => inputRef.current?.focus(), 0);
  };

  // The activity page has already fetched the row; read its title from the
  // cache rather than fetching again. Some fetchers keep the `{ data }` envelope.
  type TitleRow = { title?: string; data?: { title?: string } } | undefined;
  const cachedRow =
    ctx.kind === "activity"
      ? (queryClient.getQueryData(activityKeys.detail(ctx.slug, i18n.language)) as TitleRow)
      : undefined;
  const activityTitle = cachedRow?.title ?? cachedRow?.data?.title ?? null;
  const showIdeas = focused && visible && !chatOpen && !query && messages.length === 0 && !gated;
  const ideas = showIdeas ? ideasFor(ctx, t, activityTitle) : [];

  // Full size whenever the guest is using it.
  const compact = scrolledDown && !focused && !chatOpen && !query && !gated;
  const label = t("home.hero_ai_aria");
  const placeholder = messages.length > 0 ? t("ai_pill.follow_up") : t("ai_pill.placeholder");

  return (
    <>
      <div
        aria-hidden={!visible}
        data-open={chatOpen && visible ? "true" : "false"}
        className="mj-ai-pill pointer-events-none fixed inset-x-0 z-[101] hidden justify-center px-4 lg:flex"
      >
        <div className="relative w-full max-w-[560px]">
          <div
            className={`relative transition-[transform,opacity] duration-300 ease-out ${
              visible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"
            }`}
          >
            {/* The chat window, docked above the pill. */}
            <div
              role="dialog"
              aria-label={t("ai_concierge.title")}
              aria-hidden={!chatOpen}
              className={`absolute inset-x-0 bottom-[calc(100%+12px)] origin-bottom transition-[transform,opacity] duration-200 ease-out ${
                chatOpen && visible
                  ? "pointer-events-auto translate-y-0 scale-100 opacity-100"
                  : "translate-y-3 scale-[0.98] opacity-0"
              }`}
              style={WINDOW_STYLE}
            >
              <div
                ref={windowRef}
                className="relative flex h-[min(560px,calc(100dvh-170px))] flex-col overflow-hidden lg:h-[min(520px,calc(100vh-180px))]"
                style={WINDOW_INNER}
                dir={isRtl ? "rtl" : "ltr"}
              >
                <MatchPreviewProvider lang={lang} inTrip={inTrip} onAdd={toggleTrip} hostRef={windowRef}>
                  <header className="flex shrink-0 items-center justify-between gap-2 px-4 pb-2 pt-3">
                    <span className="flex min-w-0 items-center gap-2 whitespace-nowrap text-[14px] font-bold" style={{ color: SOFT.ink }}>
                      <Icon name="ri-sparkling-2-fill" className="text-[15px] text-[#E5B265]" aria-hidden="true" />
                      {t("ai_concierge.title")}
                    </span>
                    <div className="mj-ai-pill-tools shrink-0">
                    <ChatToolbar>
                      <ChatHistoryMenu
                        conversations={history.conversations}
                        activeId={history.activeId}
                        onNewChat={() => {
                          history.newChat();
                          setMessages([]);
                          hydratedRef.current = true;
                          window.setTimeout(() => inputRef.current?.focus(), 0);
                        }}
                        onSelect={(id) => setMessages(history.selectChat(id))}
                        onDelete={(id) => history.deleteChat(id)}
                      />
                      <button
                        type="button"
                        tabIndex={chatOpen ? 0 : -1}
                        onClick={() => {
                          setChatOpen(false);
                          void navigate({ to: "/$lang/ai-chat", params: { lang } });
                        }}
                        aria-label={t("ai_pill.open_full")}
                        title={t("ai_pill.open_full")}
                      >
                        <Icon name="ri-fullscreen-line" className="text-[14px]" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        tabIndex={chatOpen ? 0 : -1}
                        onClick={() => setChatOpen(false)}
                        aria-label={t("ai_concierge.close")}
                        title={t("ai_concierge.close")}
                      >
                        <Icon name="ri-close-line" className="text-[16px]" aria-hidden="true" />
                      </button>
                    </ChatToolbar>
                    </div>
                  </header>

                  <div
                    ref={scrollerRef}
                    className="min-h-0 flex-1 overflow-y-auto px-4 pb-4 pt-1 [scrollbar-width:thin] [scrollbar-color:rgba(17,14,57,0.18)_transparent]"
                  >
                    <div className="flex flex-col gap-4">
                      {messages.map((m) =>
                        m.kind === "user" ? (
                          <UserBubble key={m.id} id={m.id} text={m.text} />
                        ) : m.kind === "pending" ? (
                          <PendingTurn key={m.id} steps={m.steps} />
                        ) : (
                          <AssistantTurn
                            key={m.id}
                            message={m}
                            lang={lang}
                            inTrip={inTrip}
                            onAdd={toggleTrip}
                            activeTripName={null}
                            onRefine={ask}
                          />
                        ),
                      )}
                      {error && <ChatError onRetry={lastQuery ? () => ask(lastQuery) : undefined} />}
                    </div>
                  </div>

                  {gated ? (
                    <div className="shrink-0 px-4 pb-4 pt-2">
                      <ConciergeSignInGate onSignedIn={resumeAfterSignIn} compact />
                    </div>
                  ) : null}
                </MatchPreviewProvider>
              </div>
            </div>

            {/* Ideas for an empty pill, matched to the page. */}
            {showIdeas ? (
              <div
                className="pointer-events-auto absolute inset-x-0 bottom-[calc(100%+10px)]"
                style={WINDOW_STYLE}
                dir={isRtl ? "rtl" : "ltr"}
              >
                <div className="px-4 pb-3.5 pt-3" style={WINDOW_INNER}>
                  <p className="mb-2 text-[12px] font-semibold text-[#5a5d77]">{t("ai_pill.try_asking")}</p>
                  <div className="flex flex-wrap gap-2">
                    {ideas.map((idea) => (
                      <button
                        key={idea.label}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          ask(idea.query);
                          inputRef.current?.blur();
                        }}
                        className="mj-press min-h-8 rounded-full bg-white px-3.5 py-1.5 text-[13px] font-semibold text-[#110E39] ring-1 ring-[rgba(17,14,57,0.1)] transition hover:ring-[rgba(17,14,57,0.25)]"
                      >
                        {idea.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : null}

            {/* Shrinks on a spring (a small overshoot, then settle), transform
                only, so the compositor runs it and iOS does not stutter. */}
            <form
              role="search"
              autoComplete="off"
              onSubmit={submit}
              className={`relative mx-auto origin-bottom rounded-full transition-[scale,transform] duration-[520ms] ease-[cubic-bezier(0.34,1.4,0.64,1)] motion-reduce:transition-none ${
                visible ? "pointer-events-auto" : ""
              } ${compact ? "scale-[0.86]" : "scale-100"}`}
              style={BAR_GLASS}
            >
              <span aria-hidden className="pointer-events-none absolute inset-0" style={BAR_RING} />
              <span
                aria-hidden
                className={`pointer-events-none absolute inset-[3px] transition-opacity duration-[400ms] ease-out ${compact ? "opacity-0" : "opacity-100"}`}
                style={BAR_INNER}
              />
              {resuming && resumeText !== null ? (
                <AiPillResume
                  question={resumeText}
                  tabIndex={visible ? 0 : -1}
                  isRtl={isRtl}
                  onOpen={reopen}
                  // No focus hand-off to the input: focusing it reopens the
                  // thread, which is the opposite of what X asked for.
                  onDismiss={dismissResume}
                  action={
                    <button
                      type="button"
                      onClick={reopen}
                      tabIndex={visible ? 0 : -1}
                      aria-label={t("ai_pill.reopen")}
                      className="mj-press flex-shrink-0 transition-transform duration-200 hover:-translate-y-[1px]"
                      style={SEND_FRAME}
                    >
                      <span className="flex h-10 w-10 items-center justify-center text-[#110E39]" style={SEND_INNER}>
                        <Icon name="ri-arrow-up-s-line" className="text-[22px]" aria-hidden="true" />
                      </span>
                    </button>
                  }
                />
              ) : (
              <div className="relative flex h-14 items-center gap-2 ps-5 pe-1.5 lg:ps-6">
                <Icon name="ri-sparkling-2-fill" className="shrink-0 text-[16px] text-[#E5B265]" aria-hidden="true" />
                <input
                  ref={inputRef}
                  type="text"
                  name="ai_pill_search"
                  tabIndex={visible ? 0 : -1}
                  value={query}
                  disabled={gated}
                  onChange={(e) => setQuery(e.target.value)}
                  onFocus={() => {
                    setFocused(true);
                    if (messages.length > 0) setChatOpen(true);
                  }}
                  onBlur={() => setFocused(false)}
                  placeholder={placeholder}
                  aria-label={label}
                  dir={isRtl ? "rtl" : "ltr"}
                  // clamp-ok: a one-line input holding the guest's own typing; it scrolls, nothing is lost
                  className="h-full min-w-0 flex-1 truncate border-0 bg-transparent text-[16px] text-[#110E39] outline-none placeholder:text-[#5a5d77] disabled:opacity-60"
                />
                {/* Reopening is a plain button: it must still work while the
                    sign-in gate has the composer disabled. */}
                <button
                  type={reopenOnly ? "button" : "submit"}
                  onClick={reopenOnly ? () => setChatOpen(true) : undefined}
                  tabIndex={visible ? 0 : -1}
                  disabled={!reopenOnly && (loading || gated)}
                  aria-label={reopenOnly ? t("ai_pill.reopen") : label}
                  className="mj-press flex-shrink-0 transition-transform duration-200 hover:-translate-y-[1px] disabled:opacity-50 disabled:hover:translate-y-0"
                  style={SEND_FRAME}
                >
                  <span className="flex h-10 w-10 items-center justify-center text-[#110E39]" style={SEND_INNER}>
                    {loading ? (
                      <Icon name="ri-loader-4-line" className="animate-spin text-[20px]" aria-hidden="true" />
                    ) : reopenOnly ? (
                      <Icon name="ri-arrow-up-s-line" className="text-[22px]" aria-hidden="true" />
                    ) : (
                      <Icon
                        name={isRtl ? "ri-arrow-left-line" : "ri-arrow-right-line"}
                        className="text-[20px]"
                        aria-hidden="true"
                      />
                    )}
                  </span>
                </button>
              </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * Home page only: report when the hero's own search box has scrolled away, so
 * the pill can wait for it. A sentinel sits right after the hero; sentinel
 * above the viewport top means the hero is gone.
 */
export function useHeroSentinel(sentinelRef: React.RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(([entry]) => {
      if (!entry) return;
      conciergeActions.setPastHero(!entry.isIntersecting && entry.boundingClientRect.top < 0);
    });
    io.observe(el);
    return () => {
      io.disconnect();
      conciergeActions.setPastHero(false);
    };
  }, [sentinelRef]);
}
